/**
 * The server's `application/problem+json` body, verbatim from
 * `backend/internal/platform/httperr/httperr.go`.
 *
 * The UI renders `message_key`, `code` and the field errors — never `title`/`detail` as the primary
 * message (design/tokens.json §state.error). `detail` is shown as supporting text because the
 * backend writes it for operators, and `request_id` is copyable because it is the only handle
 * support has on a refusal.
 */
export interface FieldError {
  field: string;
  code: string;
  message_key: string;
}

export interface Problem {
  type: string;
  title: string;
  status: number;
  code: string;
  detail?: string;
  instance?: string;
  request_id?: string;
  occurred_at?: string;
  message_key: string;
  message_params?: Record<string, unknown>;
  retry_after_ms?: number;
  errors?: FieldError[];
}

/**
 * The 401 codes that mean THE SESSION ITSELF IS GONE, and nothing else.
 *
 * `backend/internal/platform/httperr/httperr.go` names `auth.session_expired` "doc 07 §2.8's
 * canonical 401 … an expired session, a missing one"; `iam` adds refresh reuse and an untrusted
 * device, both of which end the session server-side. Every OTHER 401 is a challenge, not a loss:
 * `auth.step_up_required` is raised BY an authenticated session (`authz.StepUpRequired`), and
 * `auth.otp_invalid` is a wrong code inside a step-up the operator is in the middle of. Signing out
 * on those unmounts the shell before the remedy can be offered — and, for a wrong TOTP digit, ends
 * a perfectly valid session.
 */
const SESSION_LOST_CODES = new Set([
  'auth.session_expired',
  'auth.refresh_reuse_detected',
  'auth.device_untrusted',
]);

/**
 * THE TYPED 5xx THAT ARE DECISIONS — the whole list, and it stays small on purpose.
 *
 * A 5xx normally says nothing about what became of the request, which is why `outcomeUnknown` reads
 * it as ignorance. But the backend raises two 5xx DELIBERATELY, for a condition it has already
 * decided: the deployment cannot serve this act at all, so the act was refused BEFORE anything ran.
 * Both say so in the same words, and both are raised above everything that could commit:
 *
 *   - `auth.step_up_unavailable` (`crm.requireStepUp`, `catalogue.requireStepUp`, README delta
 *     24.af) — "this deployment has not wired the step-up consumer, so a one-action grant cannot be
 *     spent and the act is refused rather than performed without one". 24.af adds the client rule
 *     directly: the remedy is a deployment change, and a client must NOT answer it by collecting a
 *     factor or an approval.
 *   - `idempotency.encryption_unavailable` (`Deps.requireStoredBodyCipher`) — a route whose stored
 *     reply is personal data, in a deployment with no envelope cipher. It "runs BEFORE the key is
 *     claimed and before the handler executes … so discovering at storage time that there is nothing
 *     to seal with would leave the caller a 503 for an act that already cost them their factor".
 *
 * So for exactly these, the honest reading is REFUSED: no replay is offered (there is nothing
 * stored to collect, and the next attempt meets the same refusal until a deployment changes), and
 * nothing was consumed, which is why they also keep a step-up grant rather than spending it.
 *
 * ADDING TO THIS LIST IS A CLAIM, not a convenience. A code belongs here only if the backend refuses
 * on it above every write and every consumption; anything else — an untyped 502 or 504, a
 * `internal.error` 500, a dependency that fell over mid-act — stays unknown, because the
 * over-inclusion `outcomeUnknown` documents is safe in exactly one direction and this list is the
 * other one.
 */
const DEFINITIVE_5XX_REFUSALS = new Set([
  'idempotency.encryption_unavailable',
  'auth.step_up_unavailable',
]);

/**
 * Unicode bidi controls and the Arabic letter mark.
 *
 * An opaque string from a server or a proxy is rendered next to Arabic UI text. React escapes it,
 * so there is no DOM XSS — but an unpaired `RLO`/`LRI` leaks out of the span and reverses the text
 * around it, which on a mixed Arabic/Latin refusal screen can make a message read as its opposite.
 * They are stripped, never rendered.
 */
const BIDI_CONTROLS = /[؜‎‏‪-‮⁦-⁩]/g;

/** Removes bidi control characters from untrusted text. Pair with `<bdi dir="auto">` to render it. */
export function stripBidiControls(value: string): string {
  return value.replace(BIDI_CONTROLS, '');
}

export function hasBidiControls(value: string): boolean {
  BIDI_CONTROLS.lastIndex = 0;
  return BIDI_CONTROLS.test(value);
}

/** ApiError carries the problem document plus the transport facts a screen needs. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;
  readonly method: string;
  readonly path: string;

  constructor(method: string, path: string, status: number, problem: Problem) {
    super(problem.message_key || problem.code || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.method = method;
    this.path = path;
  }

  /** The field-level errors keyed by field name, for form rendering. */
  fieldErrors(): Record<string, FieldError> {
    const out: Record<string, FieldError> = {};
    for (const e of this.problem.errors ?? []) out[e.field] = e;
    return out;
  }

  get code(): string {
    return this.problem.code;
  }

  /** A refusal that step-up can satisfy: the operator re-enters TOTP and retries the same action. */
  get needsStepUp(): boolean {
    return this.problem.code === 'auth.step_up_required' || this.problem.code === 'auth.assurance_insufficient';
  }

  /**
   * The session is gone and the shell must come down.
   *
   * This is the ONLY thing that signs an operator out. A step-up challenge, a wrong second factor
   * and any other 401 the server invents are refusals to render, not sessions to discard.
   */
  get isSessionLost(): boolean {
    if (this.status !== 401) return false;
    if (this.needsStepUp) return false;
    return SESSION_LOST_CODES.has(this.problem.code);
  }

  /** A transport failure: the request may or may not have reached the server. */
  get isTransportFailure(): boolean {
    return this.status === 0;
  }

  /** A 5xx: the server said something broke, without saying what became of the request. */
  get isServerFault(): boolean {
    return this.status >= 500 && this.status <= 599;
  }

  /**
   * THIS CLIENT CANNOT SAY WHETHER THE ACT HAPPENED (round-5 A4).
   *
   * A refusal is a DECISION: the server considered the request and declined it, so nothing was
   * written and the screen may say so. Two answers are not decisions, and they are the same
   * epistemic state as each other:
   *
   *   - a TRANSPORT FAILURE (`status === 0`) — `fetch` rejected, and the request may have committed
   *     or may never have left the tab;
   *   - a 5xx. A 500 the handler itself raised did roll its transaction back — `Deps.idempotent`
   *     abandons the claim and discards what the handler recorded — but a 5xx reaching a browser is
   *     not necessarily that one: a 502 or 504 from a proxy, a connection cut after the COMMIT and
   *     before the response was written, a crash in the middle of serving it. In every one of those
   *     the act may stand on the server, and nothing in the status line distinguishes them.
   *
   * So they are classified together, and the two rules that hang off the classification hang off it
   * together: a mutation's step-up grant is KEPT (this tab did not learn that anything spent it),
   * and the remedy offered is the EXACT replay under the same `Idempotency-Key` — which is the only
   * question that has an answer. The asymmetry justifies the over-inclusion: reading a decided 5xx
   * as unknown costs one replay that returns the same refusal, while reading a committed one as
   * decided writes a false record and throws away the key that could have corrected it.
   *
   * WITH ONE EXCEPTION, AND IT IS A NAMED ONE. The over-inclusion is over the 5xx that say nothing;
   * two typed 503s DO say something, and what they say is that the act was refused before it could
   * run — see `DEFINITIVE_5XX_REFUSALS` and `isDefinitiveRefusal`. Reading those as unknown offers a
   * replay that cannot collect anything and tells the operator a disclosure may stand on the server
   * when the server has just said it never attempted one. An untyped 502 or 504, and a 500
   * `internal.error`, remain unknown.
   *
   * AND IT IS NOT THE ONLY ANSWER THAT SETTLES NOTHING. `409 idempotency.in_progress` says the first
   * attempt is still executing, which is not ignorance but is equally not a decision about the act —
   * see `isPendingOutcome`, which carries the same two rules under its own name because a 409 has no
   * business in a getter documented as "a transport failure or a 5xx".
   */
  get outcomeUnknown(): boolean {
    if (this.isTransportFailure) return true;
    return this.isServerFault && !this.isDefinitiveRefusal;
  }

  /**
   * A 5xx WHOSE TYPED CODE IS A DECISION: the act was REFUSED, not attempted.
   *
   * Two consequences hang off it, and they are the same two `outcomeUnknown` carries, inverted:
   *
   *   - no replay surface. There is no stored outcome to collect, and the same request meets the
   *     same refusal until a deployment changes — so a screen renders the refusal and offers no
   *     "ask again under the same key".
   *   - the step-up grant is KEPT. Both codes are raised above the consumption, so the refusal is
   *     positive evidence that nothing was spent. README delta 24.af is explicit that answering
   *     `auth.step_up_unavailable` by collecting a factor is the wrong response to it.
   *
   * A transport failure can never be one: `syntheticProblem` invents its code locally, and nothing
   * answered.
   */
  get isDefinitiveRefusal(): boolean {
    return this.isServerFault && DEFINITIVE_5XX_REFUSALS.has(this.problem.code);
  }

  /** A four-eyes refusal: an approval from a second holder of the permission is missing. */
  get needsApproval(): boolean {
    return this.problem.code.startsWith('approval.') || this.problem.code === 'auth.four_eyes_required';
  }

  /**
   * A COMPARE-AND-SWAP THAT LOST — and nothing else (round-2 M8).
   *
   * This used to be "any 409, any 412", which is not what a 409 means on this API. The backend
   * answers 409 for a whole family of DECISIONS that have nothing to do with a stale version:
   * `auth.approval_pending`, `duty.seat_taken`, `queue.already_enqueued`, `expo.not_open`,
   * `idempotency.key_reuse_conflict`, `world.manifest_version_unavailable` and more. Rendering
   * every one of them as "somebody else changed this record — re-read and try again" told the
   * operator something false about each, and offered a re-read that could not possibly help.
   *
   * A lost CAS is `412 Precondition Failed`, or the explicit `version.conflict` code the domain
   * raises. That is the set this answers for, and it is the only set the re-read remedy is drawn
   * for. See `isDecidedConflict` for the rest.
   */
  get isVersionConflict(): boolean {
    return this.status === 412 || this.problem.code === 'version.conflict';
  }

  /**
   * A 409 that is a DECISION about the current state, not a lost race with another writer.
   *
   * Re-reading changes nothing here: the seat really is taken, the approval really is pending, the
   * idempotency key really was reused with a different body. The screen renders what the server
   * decided and offers no retry, because repeating a decided refusal only repeats it.
   */
  get isDecidedConflict(): boolean {
    return this.status === 409 && !this.isVersionConflict;
  }

  /** `auth.approval_pending` — an approval for this act exists and has not been acted on yet. */
  get isApprovalPending(): boolean {
    return this.problem.code === 'auth.approval_pending';
  }

  /**
   * The idempotency key is in use or was reused with a different request.
   *
   * `idempotency.in_progress` is the one conflict worth waiting on: the first attempt is still
   * executing, and the SAME key will replay its outcome once it lands. `idempotency.key_reuse_conflict`
   * is the opposite — the key is spent on a different body — and nothing may be resent under it.
   */
  get isIdempotencyInProgress(): boolean {
    return this.problem.code === 'idempotency.in_progress';
  }

  get isIdempotencyConflict(): boolean {
    return this.problem.code === 'idempotency.key_reuse_conflict';
  }

  /**
   * THE ACT IS RUNNING RIGHT NOW — the second answer that settles nothing (P13-n).
   *
   * `idempotency.go` raises `409 idempotency.in_progress` only where the first attempt's claim is
   * STILL STANDING and its owner has not been gone long enough to be reclaimed: "this really is a
   * concurrent duplicate". So the request this key names is executing as the refusal is read, and
   * the SAME key hands back its stored outcome once it lands.
   *
   * It therefore carries the two rules `outcomeUnknown` carries, for a reason of its own: a step-up
   * grant record is KEPT (nothing this tab has learned about has consumed it — and the collect that
   * would learn is what a dropped record disables), and the remedy is the EXACT replay under the
   * frozen key. Treating it as a decision deletes the only handle on an outcome that is being
   * computed at that moment.
   *
   * IT IS A SEPARATE PREDICATE, NOT A WIDENING OF `outcomeUnknown`, and the reason is that the two
   * mean different things. `outcomeUnknown` is IGNORANCE — "nothing here says what became of the
   * request", which is true of a transport failure and of a 5xx, and whose whole documented shape is
   * those two. This is KNOWLEDGE: the server said, in a typed 409, that the request has not finished.
   * They share a consequence and not a meaning, so the consequence is written where it applies —
   * `spendsGrantRecord`, and the reveal panel's open-attempt state — by reading BOTH predicates, and
   * the taxonomy keeps a 409 out of a getter that answers for 5xx and `status === 0`.
   *
   * `isDecidedConflict` deliberately stays true for it: it IS a 409 the server decided to send, and
   * `RefusalWithRemedy` draws it on the decided-conflict surface — the one that already offers the
   * collect. What this predicate names is what that refusal says about THE ACT, not about the reply.
   */
  get isPendingOutcome(): boolean {
    return this.status === 409 && this.isIdempotencyInProgress;
  }

  /**
   * A surface this package does not serve yet. The backend answers 404 for a route it does not
   * register and 503 for a dependency it refuses to fake (e.g. `world.placement_unavailable`);
   * both mean "no data exists to show", and the screen says so rather than inventing any.
   */
  get isUnavailable(): boolean {
    return this.status === 404 || this.status === 501 || this.status === 503;
  }
}

/** Builds a Problem for a response that was not problem+json (a proxy error, a dropped backend). */
export function syntheticProblem(status: number, code: string, detail: string): Problem {
  return {
    type: 'about:blank',
    title: code,
    status,
    code,
    detail,
    message_key: `error.${code}`,
  };
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}
